import type { PageTreeHierarchyItem as HierarchyItem } from '$lib/sanity/schemas';
import { captureException } from '@sentry/sveltekit';

export const findInHierarchyFromSlug = (
  hierarchyItems: HierarchyItem[],
  routeSegments: string[]
) => {
  let segment: string | undefined = undefined;
  let segmentParentId: string | undefined = undefined;

  while (routeSegments.length) {
    segment = routeSegments.shift();

    const foundSegmentInHierarchy = hierarchyItems.find((item: HierarchyItem) => {
      return (
        (item.folder?.slug?.current === segment ||
          item.page?.slug?.current === segment ||
          item.file?.slug?.current === segment) &&
        item.parent === segmentParentId
      );
    });

    if (foundSegmentInHierarchy) {
      if (!routeSegments.length) {
        return foundSegmentInHierarchy;
      }
      segmentParentId = foundSegmentInHierarchy.id;
    }
  }
};

type HierarchyMap = Record<string, { slug: string; parent: string | null | undefined }>;

const createHierarchyMap = (hierarchyItems: HierarchyItem[]) => {
  const hierarchyMap: HierarchyMap = {};
  hierarchyItems.forEach((hierarchyItem) => {
    let slug = '';
    if (hierarchyItem.page) {
      slug = hierarchyItem.page.slug.current;
    } else if (hierarchyItem.file) {
      slug = hierarchyItem.file.slug.current;
    } else if (hierarchyItem.folder) {
      slug = hierarchyItem.folder.slug.current;
    }

    hierarchyMap[hierarchyItem.id] = {
      slug,
      parent: hierarchyItem.parent
    };
  });
  return hierarchyMap;
};

export const buildFullPath = (itemId: string, hierarchyMap: HierarchyMap): string => {
  const item = hierarchyMap[itemId];
  if (!item) {
    return '';
  }
  if (!item.parent) {
    return `/${item.slug}`;
  }
  return `${buildFullPath(item.parent, hierarchyMap)}/${item.slug}`;
};

export const generatePagePathFromSlug = (
  pageSlug: string = '',
  hierarchyItems: HierarchyItem[]
) => {
  if (!hierarchyItems) {
    captureException('Page tree hierarchy items not provided to generatePagePathFromSlug');
    return '';
  }

  const pageHierarchyItem = hierarchyItems.find(
    (hierarchyItem) =>
      hierarchyItem.page?.slug.current === pageSlug || hierarchyItem.file?.slug.current === pageSlug
  )!;

  if (!pageHierarchyItem) {
    captureException(`Page slug: "${pageSlug}" not found in page tree hierarchy`);

    return `/${pageSlug}`;
  }

  const hierarchyMap = createHierarchyMap(hierarchyItems);

  return buildFullPath(pageHierarchyItem.id, hierarchyMap);
};

export const buildFullPathMap = (hierarchyItems: HierarchyItem[]) => {
  const hierarchyMap = createHierarchyMap(hierarchyItems);
  const resolvedPathmap: Record<string, string> = {};

  Object.keys(hierarchyMap).forEach((itemId) => {
    resolvedPathmap[itemId] = buildFullPath(itemId, hierarchyMap);
  });

  return resolvedPathmap;
};
